import useParentResizeDnd from '@/data/hook/shared/dnd/useParentResizeDnd';
import { Model, Ordenable } from '@/models/Model';
import { nestedDropHandler } from '@/utils/dragdrop';
import { reorder, reorderMap } from '@/utils/list';
import { DropResult } from '@hello-pangea/dnd';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

type BaseType = Model & Ordenable;

type UseNestedOptimisticReorderProps<P extends BaseType, C extends BaseType> = {
  parents: P[];
  childrenMap: Record<string, C[]>;
  parentType: string;
  parentPrefix: string;
  isReordering: boolean;
  onReorder: (parents: P[], childrenMap: Record<string, C[]>) => void;
};

const useNestedOptimisticReorder = <P extends BaseType, C extends BaseType>({
  parents: originalParents,
  childrenMap: originalChildrenMap,
  parentType,
  parentPrefix,
  isReordering,
  onReorder,
}: UseNestedOptimisticReorderProps<P, C>) => {
  const [localParents, setLocalParents] = useState<P[]>(originalParents);
  const [localChildrenMap, setLocalChildrenMap] =
    useState<Record<string, C[]>>(originalChildrenMap);
  const [justReordered, setJustReordered] = useState(false);

  const { isDraggingParent, onBeforeCapture, resetDragStyles } =
    useParentResizeDnd({ parentPrefix });

  useEffect(() => {
    if (isReordering && justReordered) setJustReordered(false);
  }, [isReordering, justReordered]);

  useEffect(() => {
    if (isReordering || justReordered) return;
    if (!isEqual(localParents, originalParents)) {
      setLocalParents(originalParents);
    }
  }, [originalParents, isReordering, justReordered, localParents]);

  useEffect(() => {
    if (isReordering || justReordered) return;
    if (!isEqual(localChildrenMap, originalChildrenMap)) {
      setLocalChildrenMap(originalChildrenMap);
    }
  }, [originalChildrenMap, isReordering, justReordered, localChildrenMap]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      resetDragStyles();
      const updated = nestedDropHandler(
        result,
        localParents,
        localChildrenMap,
        parentType,
      );
      if (!updated) return;
      const newLocalParents = reorder(
        updated.parents,
        updated.parents.map(p => p.id),
      );
      const newLocalChildrenMap = reorderMap(updated.childrenMap);
      setLocalParents(newLocalParents);
      setLocalChildrenMap(newLocalChildrenMap);
      setJustReordered(true);
      onReorder(updated.parents, updated.childrenMap);
    },
    [localParents, localChildrenMap, parentType, resetDragStyles, onReorder],
  );

  return {
    optimisticParents: localParents,
    optimisticChildrenMap: localChildrenMap,
    handleDragEnd,
    isDraggingParent,
    onBeforeCapture,
  };
};

export default useNestedOptimisticReorder;
